import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { useDrop } from "react-dnd";
import {
  addElement,
 
  setSelectedElements,
  clearSelection,
  saveToHistory,
} from "../../../store/warehouseSlice";

const GridCanvas = () => {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const {
    currentWarehouse,
    elements,
    selectedElements,
    zoom,
    pan,
    gridSize,
    showGrid,
    snapToGrid,
  } = useSelector((state) => state.warehouse);

  // Calculate canvas size
  useEffect(() => {
    const updateSize = () => {
      if (canvasRef.current) {
        setCanvasSize({
          width: canvasRef.current.offsetWidth,
          height: canvasRef.current.offsetHeight,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Drop handler for adding new elements
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ["RACK", "ZONE", "WALL", "DOOR", "AISLE", "COLUMN"],
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();
      const x = (offset.x - canvasRect.left - pan.x) / zoom / gridSize;
      const y = (offset.y - canvasRect.top - pan.y) / zoom / gridSize;

      // Snap to grid if enabled
      const snappedX = snapToGrid ? Math.round(x) : x;
      const snappedY = snapToGrid ? Math.round(y) : y;

      // Create new element
      const typeNameMap = {
        RACK: 'Regal',
        ZONE: 'Zone',
        WALL: 'Wand',
        DOOR: 'Tür',
        AISLE: 'Gang',
        COLUMN: 'Säule'
      };
      const newElement = {
        id: `${item.type.toLowerCase()}-${Date.now()}`,
        type: item.type.toLowerCase(),
        name: `${typeNameMap[item.type] || item.type} ${elements.length + 1}`,
        code: `${item.type[0]}${elements.length + 1}`,
        gridPosition: {
          x: Math.max(0, snappedX),
          y: Math.max(0, snappedY),
          width: item.defaultWidth || 2,
          height: item.defaultHeight || 2,
        },
        color: item.defaultColor || "#4361ee",
        ...(item.type === "RACK" && {
          rackType: "shelf",
          numberOfShelves: 3,
          rotation: 0,
        }),
        ...(item.type === "ZONE" && {
          zoneType: "storage",
        }),
      };

      dispatch(addElement(newElement));
      dispatch(saveToHistory());
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));



  // Handle element click for selection
  const handleElementClick = (elementId, event) => {
    event.stopPropagation();

    if (event.ctrlKey || event.metaKey) {
      // Multi-select
      if (selectedElements.includes(elementId)) {
        dispatch(
          setSelectedElements(selectedElements.filter((id) => id !== elementId))
        );
      } else {
        dispatch(setSelectedElements([...selectedElements, elementId]));
      }
    } else {
      // Single select
      dispatch(setSelectedElements([elementId]));
    }
  };

  // Handle canvas click (clear selection)
  const handleCanvasClick = () => {
    dispatch(clearSelection());
  };

  // Draw grid
  const drawGrid = () => {
    if (!showGrid || !currentWarehouse) return null;

    const { width, height } = currentWarehouse.dimensions;
    const cellSize = gridSize * zoom;
    const lines = [];

    // Vertical lines
    for (let x = 0; x <= width; x++) {
      lines.push(
        <line
          key={`v-${x}`}
          x1={x * cellSize + pan.x}
          y1={pan.y}
          x2={x * cellSize + pan.x}
          y2={height * cellSize + pan.y}
          stroke="#e0e0e0"
          strokeWidth="1"
        />
      );
    }

    // Horizontal lines
    for (let y = 0; y <= height; y++) {
      lines.push(
        <line
          key={`h-${y}`}
          x1={pan.x}
          y1={y * cellSize + pan.y}
          x2={width * cellSize + pan.x}
          y2={y * cellSize + pan.y}
          stroke="#e0e0e0"
          strokeWidth="1"
        />
      );
    }

    return lines;
  };

  // Render warehouse elements
  const renderElements = () => {
    return elements.map((element) => {
      const isSelected = selectedElements.includes(element.id);
      const x = element.gridPosition.x * gridSize * zoom + pan.x;
      const y = element.gridPosition.y * gridSize * zoom + pan.y;
      const width = element.gridPosition.width * gridSize * zoom;
      const height = element.gridPosition.height * gridSize * zoom;

      return (
        <g key={element.id}>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={element.color}
            fillOpacity={element.type === "zone" ? 0.3 : 0.8}
            stroke={isSelected ? "#f39c12" : "#333"}
            strokeWidth={isSelected ? 3 : 1}
            cursor="move"
            onClick={(e) => handleElementClick(element.id, e)}
            style={{ transition: "stroke 0.2s" }}
          />
          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#fff"
            fontSize={12}
            fontWeight="bold"
            pointerEvents="none"
          >
            {element.name}
          </text>
          {element.code && (
            <text
              x={x + width / 2}
              y={y + height / 2 + 15}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#fff"
              fontSize={10}
              pointerEvents="none"
            >
              {element.code}
            </text>
          )}
        </g>
      );
    });
  };

  if (!currentWarehouse) {
    return <Box>No warehouse selected</Box>;
  }

  return (
    <Box
      ref={drop}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        backgroundColor: isOver ? "#f0f0f0" : "#fff",
        cursor: "default",
      }}
    >
      <div ref={canvasRef} style={{ width: "100%", height: "100%" }} onClick={handleCanvasClick}>
        <svg width="100%" height="100%" style={{ display: "block" }}>
          {drawGrid()}
          {renderElements()}
        </svg>
      </div>

      {/* Canvas Info */}
      <Box
        sx={{
          position: "absolute",
          bottom: 10,
          left: 10,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          padding: "8px 12px",
          borderRadius: 1,
          fontSize: 12,
          fontFamily: "monospace",
        }}
      >
        Warehouse: {currentWarehouse.dimensions.width} × {currentWarehouse.dimensions.height}{" "}
        {currentWarehouse.dimensions.unit} | Elements: {elements.length} | Selected:{" "}
        {selectedElements.length}
      </Box>
    </Box>
  );
};

export default GridCanvas;
